import { trackItemSelect, trackItemView, trackItemViewList, trackAddToCart } from './event-trigger';
import type { EcommerceItem, ProductCategory, TrackFigure, TrackFigureData } from './types';
import type { Figure } from '$lib/server/figures/types';
import type { StickerShopMeta } from '$lib/types/StickerShopMeta';
import { getLocalizedInfo } from '$lib/utilities/localization';

const getFigureTagTitle = (figure: Figure, stickerShopMeta: StickerShopMeta) =>
  stickerShopMeta.figureTags.find((tag) => Number(tag.id) === figure.tag_id)?.title ||
  'Uncategorized';

const libraryItemDefaults: Record<string, string> = {
  productCategory: 'library',
  productType: 'single',
  shape: 'sticker_individual',
  material: 'white',
  laminate: 'glossy_uv',
} as const;

function toGtmEcommerceItem(
  { figure, stickerShopMeta, localization }: TrackFigure,
  index: number = 1,
): EcommerceItem | undefined {
  if (!figure || !figure.product_item.attributes) {
    return undefined;
  }
  const dimensions =
    !isNaN(Number(figure.product_item.attributes.width_mm)) &&
    !isNaN(Number(figure.product_item.attributes.height_mm))
      ? `${Math.round(Number(figure.product_item.attributes.width_mm) / 10)}x${Math.round(
          Number(figure.product_item.attributes.height_mm) / 10,
        )} `
      : '';

  const priceItem = {
    currency: localization.currencyCode,
    unitPrice:
      parseFloat(getLocalizedInfo(figure, localization)?.price?.unitPriceFormatted ?? '0') || 0,
  };

  return {
    item_id: figure.product_item.attributes.figure_id,
    item_name: figure.title,
    item_category: libraryItemDefaults.productCategory as ProductCategory,
    item_category2: getFigureTagTitle(figure, stickerShopMeta),
    item_category3: libraryItemDefaults.productType,
    item_category4: dimensions,
    item_category5: libraryItemDefaults.shape,
    item_category6: libraryItemDefaults.laminate,
    item_variant: libraryItemDefaults.material,
    quantity: figure.product_item.attributes.quantity,
    price: priceItem.unitPrice,
    currency: priceItem.currency,
    index,
  } as const;
}

export function trackCategoryItemViewList({
  figureData,
  stickerShopMeta,
  localization,
}: TrackFigureData) {
  trackItemViewList(
    () =>
      figureData?.data
        .map((figure, index) => {
          const commerceItem = toGtmEcommerceItem({ figure, stickerShopMeta, localization }, index);
          if (!commerceItem) {
            return;
          }
          return commerceItem;
        })
        .filter(Boolean) ?? [],
  );
}

export function trackFigureItemSelect(trackFigure: TrackFigure) {
  const commerceItem = toGtmEcommerceItem(trackFigure);
  if (!commerceItem) {
    return;
  }
  trackItemSelect(commerceItem);
}

export function trackFigureItemView(trackFigure: TrackFigure) {
  const commerceItem = toGtmEcommerceItem(trackFigure);
  if (!commerceItem) {
    return;
  }
  trackItemView(commerceItem);
}

export function trackFigureAddToCart(trackFigure: TrackFigure) {
  const commerceItem = toGtmEcommerceItem(trackFigure);
  if (!commerceItem) {
    return;
  }
  trackAddToCart(commerceItem);
}
